import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {RelayEnvironmentProvider} from '@github-ui/relay-environment'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Link as RouterLink, useLocation} from '@github-ui/react-router'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {RepoIssuesSidebarLayoutEnabledContext} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {SavedViewsResponse, UserShortcut} from '@github-ui/saved-views'
import {canShowCreateAction} from '@github-ui/saved-views/utils/can-show-create-action'
import {ViewsBackLink} from '@github-ui/saved-views/components/ViewsBackLink'
import {BUTTON_LABELS} from '@github-ui/saved-views/constants'
import {RepositorySavedViewContent} from '@github-ui/saved-views/components/detail/RepositorySavedViewContent'
import {ViewsIndexContent} from '@github-ui/saved-views/components/index/ViewsIndexContent'
import {RepositoryViewNewContent} from '@github-ui/saved-views/components/new/RepositoryViewNewContent'
import {useConfirmDiscardChanges} from '@github-ui/saved-views/hooks/use-confirm-discard-changes'
import {useSavedViewEditor} from '@github-ui/saved-views/hooks/use-saved-view-editor'
import {useNavigate} from '@github-ui/use-navigate'
import {UserContext} from '@github-ui/use-user'
import {Button} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {getIssuesRelayEnvironment} from '../components/issues-relay-environment'
import {TEMP_FIELDS_PAYLOAD} from '../components/board/issues/proto-to-board-payload-mapper'
import {RepositorySavedViewOptionsMenu} from '../components/RepositorySavedViewOptionsMenu'
import {useRepositorySavedViewOptions} from '../components/use-repository-saved-view-options'
import {VisibleFieldsProvider} from '../contexts/views/VisibleFieldsProvider'
import {FieldsProvider} from '../contexts/views/FieldsProvider'
import {issuesLayoutRoute, layoutCurrentUser} from './issues-layout-route'
import {useRenderSidebar} from './render-issues-sidebar'
import {savedViewsRoute} from './saved-views-routes'
import {RepositorySavedViewBoard} from './RepositorySavedViewBoard'
import {TanStackSavedViewIssues} from './TanStackSavedViewIssues'

const INITIAL_SHORTCUT: UserShortcut = {
  id: '',
  name: '',
  searchType: 'issues',
  query: 'is:issue state:open',
  description: '',
  icon: 'bookmark',
  color: 'gray',
}

function useSavedViewsLayout(initialSavedViews?: SavedViewsResponse) {
  const {owner, repo} = useRouteParams(issuesLayoutRoute)
  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  return {
    owner,
    repo,
    layoutPayload,
    renderSidebar: useRenderSidebar(owner, repo, layoutPayload, initialSavedViews),
  }
}

export function SavedViews() {
  const {data: savedViews} = useRouteQuery(savedViewsRoute, 'mainQuery')
  const {owner, repo, layoutPayload, renderSidebar} = useSavedViewsLayout(savedViews)
  const newViewUrl = `/${owner}/${repo}/issues/views/new`
  const teamPersonalEnabled = isFeatureEnabled('issue_pinned_views_team_vs_personal')
  const newViewAction = canShowCreateAction({savedViews, teamPersonalEnabled}) ? (
    <Button as={RouterLink} to={newViewUrl} variant="primary">
      {BUTTON_LABELS.newView}
    </Button>
  ) : undefined

  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <RepoIssuesPageLayout
        title="Views"
        dialogTitle="Views"
        actions={newViewAction}
        renderSidebar={renderSidebar}
        initialExpanded={layoutPayload?.sidebarExpanded}
      >
        <UserContext value={layoutCurrentUser(layoutPayload)}>
          <ViewsIndexContent
            owner={owner}
            repo={repo}
            newViewUrl={newViewUrl}
            pinEnabled={layoutPayload?.pinnedViewsEnabled ?? false}
            teamPersonalEnabled={teamPersonalEnabled}
            initialData={savedViews}
          />
        </UserContext>
      </RepoIssuesPageLayout>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

export function SavedViewNew() {
  const {owner, repo, layoutPayload, renderSidebar} = useSavedViewsLayout()
  const navigate = useNavigate()
  const editor = useSavedViewEditor({initialShortcut: INITIAL_SHORTCUT})
  const {confirmDiscardIfDirty} = useConfirmDiscardChanges({isDirty: editor.isDirty})
  const viewsPath = `/${owner}/${repo}/issues/views`

  const handleBackClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    async event => {
      if (event.defaultPrevented || event.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !editor.isDirty) return

      event.preventDefault()
      if (await confirmDiscardIfDirty()) navigate(viewsPath)
    },
    [confirmDiscardIfDirty, editor.isDirty, navigate, viewsPath],
  )

  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <RepoIssuesPageLayout
        dialogTitle="Views"
        title="Create view"
        contextArea={<ViewsBackLink viewsPath={viewsPath} onClick={handleBackClick} />}
        renderSidebar={renderSidebar}
        initialExpanded={layoutPayload?.sidebarExpanded}
      >
        <RepositoryViewNewContent
          editor={editor}
          confirmDiscardIfDirty={confirmDiscardIfDirty}
          onBackLinkClick={handleBackClick}
          IssuesRenderer={TanStackSavedViewIssues}
        />
      </RepoIssuesPageLayout>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

export function SavedView() {
  const {owner, repo, layoutPayload, renderSidebar} = useSavedViewsLayout()
  const {pathname} = useLocation()
  const boardFeatureEnabled = isFeatureEnabled('repo_issues_board_view')
  const teamPersonalEnabled = isFeatureEnabled('issue_pinned_views_team_vs_personal')
  const IssuesRenderer = boardFeatureEnabled ? SavedViewIssuesForLayout : TanStackSavedViewIssues
  const currentUser = useMemo(() => layoutCurrentUser(layoutPayload), [layoutPayload])

  const content = (
    <RepositorySavedViewContent
      IssuesRenderer={IssuesRenderer}
      pinEnabled={layoutPayload?.pinnedViewsEnabled ?? false}
      perVisibilityPinCapEnabled={teamPersonalEnabled}
      renderSidebar={renderSidebar}
      renderCreateIssueButton={() => <RepositoryCreateIssueButton owner={owner} repo={repo} />}
      filterBarActions={boardFeatureEnabled ? <RepositorySavedViewOptionsMenu /> : undefined}
    />
  )

  // The board FF gates the whole view-options experience: when it's on the menu renders
  // above (via `filterBarActions`) and needs both providers regardless of the current
  // layout. Off, none of it mounts.
  return (
    <RepoIssuesSidebarLayoutEnabledContext value>
      <UserContext value={currentUser}>
        {boardFeatureEnabled ? (
          <FieldsProvider data={TEMP_FIELDS_PAYLOAD}>
            <VisibleFieldsProvider key={pathname}>{content}</VisibleFieldsProvider>
          </FieldsProvider>
        ) : (
          content
        )}
      </UserContext>
    </RepoIssuesSidebarLayoutEnabledContext>
  )
}

function RepositoryCreateIssueButton({owner, repo}: {owner: string; repo: string}) {
  const environment = getIssuesRelayEnvironment()
  const navigate = useNavigate()

  return (
    <RelayEnvironmentProvider environment={environment}>
      <CreateIssueButton
        label="New issue"
        navigate={navigate}
        optionConfig={{
          issueCreateArguments: {repository: {owner, name: repo}},
          showRepositoryPicker: false,
          showFullScreenButton: true,
          canBypassTemplateSelection: true,
          navigate,
        }}
      />
    </RelayEnvironmentProvider>
  )
}

/** Renders the board or list issues surface based on the selected layout. */
function SavedViewIssuesForLayout({
  owner,
  repo,
  query,
  preheatEnabled,
}: {
  owner: string
  repo: string
  query: string
  preheatEnabled?: boolean
}) {
  const {layout} = useRepositorySavedViewOptions()

  return layout === 'board' ? (
    <RepositorySavedViewBoard owner={owner} repo={repo} query={query} />
  ) : (
    <TanStackSavedViewIssues owner={owner} repo={repo} query={query} preheatEnabled={preheatEnabled} />
  )
}
